import { Flex, UseDisclosureReturn } from '@chakra-ui/react';
import { EntityNoteModel } from '../../globalModels/entityNotes/entityNoteModel';
import { EntityNoteType } from '../../enums/common/EntityNoteType';
import { ApiEndpoints } from '../../constants/endpoints/apiEndpoints.ts';

import { BasePopover, FormContainer, createFieldHelper, useHookForm, useStandardToast } from '@cks-internal/react-base';
import { useEntityNoteMutation } from './apis/useEntityNoteMutation';
import { EntityNoteQueryType } from '../../enums/common/EntityNoteQueryType';
import { companyCrmFlexGap } from '../company/overview/utils/common';
import { PropsWithChildren, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  formatTimeForBackend,
  formatTimeForDisplay
} from '../activities/editActivity/toolbar/followUpModal/utils/followUpFormatters';

type IEntityNotesDialog = PropsWithChildren & {
  disclosure: UseDisclosureReturn;
  model?: EntityNoteModel;
  id?: number;
  queryType: EntityNoteQueryType;
};

type EntityNoteFormModel = EntityNoteModel & {
  expiryDate?: string;
  expiryTime?: string;
};

export const EntityNotesPopover = (props: IEntityNotesDialog) => {
  const toast = useStandardToast();

  const initialModel: EntityNoteFormModel = props.model
    ? (() => {
        return {
          ...props.model,
          expiryDate: props.model.ExpiresOnDate || '',
          expiryTime: props.model.ExpiresOnTime ? formatTimeForDisplay(props.model.ExpiresOnTime) : ''
        };
      })()
    : {
        IsDisabled: false,
        IsPinned: false,
        Note: '',
        EntityNoteType: EntityNoteType.Basic,
        ExpiresOnDate: undefined,
        ExpiresOnTime: null,
        ExpiresOnTimeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        expiryDate: '',
        expiryTime: ''
      };

  const { formMethods } = useHookForm<EntityNoteFormModel>(initialModel);
  const helper = createFieldHelper<EntityNoteFormModel>(formMethods);
  const createNoteMutation = useEntityNoteMutation();
  const isPinned = props.model?.IsPinned ?? false;

  const isValidTimeInput = (time?: string) => {
    if (!time) return true;
    return dayjs(`2000-01-01 ${time}`).isValid();
  };

  // Ensure edit popover always reflects latest expiry values when opened
  useEffect(() => {
    if (props.model && props.disclosure.open) {
      formMethods.reset({
        ...props.model,
        expiryDate: props.model.ExpiresOnDate || '',
        expiryTime: props.model.ExpiresOnTime ? formatTimeForDisplay(props.model.ExpiresOnTime) : ''
      });
    }
  }, [props.model, props.disclosure.open]);

  const baseFields = [
    helper.hiddenInput('Id'),
    helper
      .textArea('Note')
      .withValidation({ required: { message: 'A Note is required' } })
      .withLabel('Note'),
    helper
      .selectInputQueryItems('EntityNoteType', { searchUrl: ApiEndpoints.selectOptions.enum('EntityNoteType', true) })
      .withLabel('Note Type')
  ];

  const expiryFields = [
    helper
      .dateInput('expiryDate')
      .withLabel('Expires On Date')
      .withRenderIf(() => isPinned)
      .withValidation({
        validate: (date, formValues) => {
          const time = formValues.expiryTime;
          if (time && !date) {
            return 'Expiry date is required when a time is provided';
          }
          return undefined;
        }
      }),
    helper
      .timeInput('expiryTime')
      .withLabel('Expires On Time')
      .withRenderIf(() => isPinned)
      .withValidation({
        validate: (time) => {
          if (!time) return undefined;
          if (!isValidTimeInput(time)) return 'Please enter a valid time';
          return undefined;
        }
      })
  ];

  const onSubmit = async (formModel: EntityNoteFormModel) => {
    const { expiryDate, expiryTime, ...rest } = formModel;
    const backendTime = expiryTime ? formatTimeForBackend(expiryTime) : null;

    const modelToSave: EntityNoteModel = {
      ...rest,
      ExpiresOnDate: expiryDate || undefined,
      ExpiresOnTime: backendTime,
      ExpiresOnTimeZoneId: rest.ExpiresOnTimeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    try {
      await createNoteMutation.mutateAsync({ noteType: props.queryType, id: props.id, model: modelToSave });
      formMethods.reset();
      props.disclosure.onClose();
      toast.successToast('Success', 'Updated Note');
    } catch {
      // Errors are handled by global axios/react-query handlers
    }
  };

  const handleClose = () => {
    formMethods.reset();
    props.disclosure.onClose();
  };

  const handleOk = async () => {
    const isValid = await formMethods.trigger();
    if (!isValid) return;
    await onSubmit(formMethods.getValues());
  };

  return (
    <BasePopover
      trigger={props.children}
      contentProps={{ width: '420px' }}
      disclosure={props.disclosure}
      onClose={handleClose}
      onCancel={handleClose}
      onOk={handleOk}
      isOkayDisabled={createNoteMutation.isPending || !formMethods.formState.isValid}
      okayButtonText='Save'>
      <FormContainer<EntityNoteFormModel> methods={formMethods} hideControls hideDebugOnLocal>
        <Flex gap={companyCrmFlexGap} flexDir={'column'} w={'100%'}>
          {helper.renderFieldHelperInput('EntityNoteType', baseFields)}
          {helper.renderFieldHelperInput('Note', baseFields)}
          {isPinned && (
            <Flex gap={companyCrmFlexGap} flexDir={'column'} w={'100%'}>
              {helper.renderFieldHelperInput('expiryDate', expiryFields)}
              {helper.renderFieldHelperInput('expiryTime', expiryFields)}
            </Flex>
          )}
        </Flex>
      </FormContainer>
    </BasePopover>
  );
};
